import { useMemo, useState, type ReactElement } from 'react';

import { ReportPresetDialog } from '../components/report-presets';
import {
  buildReportProjection,
  type ReportChartSeries,
  type ReportProjectedRow,
  type ReportValue,
} from '../integration/report-projection';
import { useProjectStore } from '../store/project-store';
import type { CreateReportPresetInput, ReportFieldId, ReportPreset } from '../store/types';

const FIELD_LABELS: Record<ReportFieldId, string> = {
  name: '名稱',
  wbs: 'WBS',
  start: '開始',
  finish: '完成',
  duration: '工期（分鐘）',
  work: '工時（分鐘）',
  percentComplete: '完成百分比',
  cost: '成本',
  actualCost: '實際成本',
  remainingCost: '剩餘成本',
  variance: '成本差異',
  isCritical: '要徑',
  isMilestone: '里程碑',
  type: '類型',
  group: '群組',
  maxUnits: '最大單位',
  isOverallocated: '過度分配',
  isBudget: '預算資源',
};

function formatValue(value: ReportValue): string {
  if (value === null) return '—';
  if (typeof value === 'boolean') return value ? '是' : '否';
  return typeof value === 'number' ? value.toLocaleString('zh-TW') : value;
}

function TableReport({ preset, rows, groups }: {
  readonly preset: ReportPreset;
  readonly rows: readonly ReportProjectedRow[];
  readonly groups: ReturnType<typeof buildReportProjection>['groups'];
}): ReactElement {
  return (
    <div className="min-h-0 overflow-auto rounded border border-line">
      <table className="w-full min-w-max border-collapse text-sm">
        <thead className="sticky top-0 bg-raised text-left">
          <tr>{preset.fields.map((field) => <th key={field} className="border-b border-line px-3 py-2">{FIELD_LABELS[field]}</th>)}</tr>
        </thead>
        <tbody>
          {groups.flatMap((group) => [
            ...(group.label === null ? [] : [
              <tr key={`${group.key}-header`}><th colSpan={preset.fields.length} className="bg-accent-soft px-3 py-2 text-left text-accent-deep">{group.label}</th></tr>,
            ]),
            ...group.rows.map((row) => (
              <tr key={row.id} className="odd:bg-surface even:bg-raised/50">
                {preset.fields.map((field) => <td key={field} className="border-b border-hairline px-3 py-2">{formatValue(row.values[field] ?? null)}</td>)}
              </tr>
            )),
          ])}
          {rows.length === 0 ? <tr><td colSpan={preset.fields.length} className="px-3 py-8 text-center text-fg-muted">沒有符合篩選條件的資料。</td></tr> : null}
        </tbody>
      </table>
    </div>
  );
}

function BarSeries({ series }: { readonly series: ReportChartSeries }): ReactElement {
  const maximum = Math.max(1, ...series.points.map((point) => Math.abs(point.value ?? 0)));
  return (
    <section aria-label={FIELD_LABELS[series.field]} className="rounded border border-line p-3">
      <h3 className="mb-3 font-medium">{FIELD_LABELS[series.field]}</h3>
      <div className="space-y-2">
        {series.points.map((point) => (
          <div key={point.rowId} className="grid grid-cols-[minmax(7rem,1fr)_3fr_auto] items-center gap-2 text-xs">
            <span className="truncate" title={point.label}>{point.label}</span>
            <div className="h-4 rounded bg-raised"><div data-report-bar className="h-full rounded bg-accent-bar" style={{ width: `${Math.abs(point.value ?? 0) / maximum * 100}%` }} /></div>
            <span>{formatValue(point.value)}</span>
          </div>
        ))}
        {series.points.length === 0 ? <p className="text-fg-muted">沒有符合篩選條件的資料。</p> : null}
      </div>
    </section>
  );
}

export function ReportCenterView({ readOnly = false }: { readonly readOnly?: boolean }): ReactElement {
  const project = useProjectStore((state) => state.project);
  const presets = useProjectStore((state) => state.reportPresets);
  const activeId = useProjectStore((state) => state.activeReportPresetId);
  const selectPreset = useProjectStore((state) => state.selectReportPreset);
  const createPreset = useProjectStore((state) => state.createReportPreset);
  const updatePreset = useProjectStore((state) => state.updateReportPreset);
  const duplicatePreset = useProjectStore((state) => state.duplicateReportPreset);
  const deletePreset = useProjectStore((state) => state.deleteReportPreset);
  const [dialog, setDialog] = useState<'new' | 'edit' | null>(null);
  const activePreset = presets.find((preset) => preset.id === activeId) ?? null;
  const projection = useMemo(
    () => activePreset === null ? null : buildReportProjection(project, activePreset),
    [activePreset, project],
  );

  const savePreset = (input: CreateReportPresetInput | ReportPreset): void => {
    if ('id' in input && typeof input.id === 'string') {
      updatePreset(input.id, input);
    } else {
      const id = createPreset(input);
      if (id !== null) selectPreset(id);
    }
    setDialog(null);
  };

  return (
    <section data-testid="report-center-view" aria-readonly={readOnly} className="flex h-full min-h-0 flex-col gap-3 bg-canvas text-fg">
      <style>{`@media print { @page { size: ${activePreset?.printOrientation ?? 'landscape'}; } }`}</style>
      <header className="print:hidden flex flex-wrap items-center gap-2 rounded border border-line bg-surface p-3">
        <label className="flex items-center gap-2 text-sm">報表
          <select aria-label="目前報表" value={activeId ?? ''} onChange={(event) => selectPreset(event.target.value || null)} className="rounded border border-line bg-surface px-2 py-1">
            {presets.map((preset) => <option key={preset.id} value={preset.id}>{preset.name}</option>)}
          </select>
        </label>
        <button type="button" disabled={readOnly} onClick={() => setDialog('new')} className="rounded border border-line px-2 py-1 text-sm disabled:opacity-50">建立報表</button>
        <button type="button" disabled={readOnly || activePreset === null} onClick={() => setDialog('edit')} className="rounded border border-line px-2 py-1 text-sm disabled:opacity-50">設定</button>
        <button type="button" disabled={activePreset === null} onClick={() => window.print()} className="ml-auto rounded bg-accent px-3 py-1 text-sm text-white disabled:opacity-50">列印</button>
      </header>

      {activePreset === null || projection === null ? (
        <div className="grid flex-1 place-items-center rounded border border-dashed border-line text-sm text-fg-muted">
          <div className="text-center"><p>尚未建立報表。</p>{readOnly ? null : <button type="button" onClick={() => setDialog('new')} className="mt-2 rounded bg-accent px-3 py-1 text-white">建立第一份報表</button>}</div>
        </div>
      ) : (
        <article data-testid="report-preview" className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden rounded border border-line bg-surface p-4">
          <div><h2 className="text-xl font-semibold">{activePreset.name}</h2><p className="text-sm text-fg-muted">{activePreset.dataset === 'Tasks' ? '任務' : '資源'} · {projection.rows.length} 筆</p></div>
          {projection.table !== null ? <TableReport preset={activePreset} rows={projection.rows} groups={projection.table.groups} /> : null}
          {projection.chart !== null ? <div className="min-h-0 space-y-3 overflow-auto">{projection.chart.series.map((series) => <BarSeries key={series.field} series={series} />)}</div> : null}
          {projection.comparison !== null ? <div className="grid min-h-0 gap-3 overflow-auto lg:grid-cols-2"><BarSeries series={projection.comparison.left} /><BarSeries series={projection.comparison.right} /></div> : null}
        </article>
      )}

      <ReportPresetDialog
        isOpen={!readOnly && dialog !== null}
        preset={dialog === 'edit' ? activePreset : null}
        existingPresets={presets}
        onCancel={() => setDialog(null)}
        onSave={savePreset}
        onDuplicate={(id) => { duplicatePreset(id); setDialog(null); }}
        onDelete={(id) => { deletePreset(id); setDialog(null); }}
      />
    </section>
  );
}
